import { Show, For, useContext, createSignal } from 'solid-js';
import { pdfStore, Collection } from '../../pdf-store';
import { CollectionSingle } from './collection-single';
import { AppContext } from '../../../../app-context-provider/app-context-provider';
import { Heading } from '../../../../ui-components/heading/heading';
import { Button } from '../../../../ui-components/button/button';
import { EmptyState } from '../empty-state/empty-state';

export const CollectionsList = (props: {
    deleteCollection: (collectionId: string) => void;
    editCollectionAsArrangement: (collection: Collection) => void;
    reset: () => void;
}) => {

    const {
        localize,
        userState,
        siteInfo,
    } = useContext(AppContext);

    const [ expandAll, setExpandAll ] = createSignal(false);
    const [ expandTimestamp, setExpandTimestamp ] = createSignal(0); // Timestamp to force re-render when expanding/collapsing all collections

    const expandAllCollections = () => {
        setExpandTimestamp(Date.now());
        setExpandAll(true);
    };

    const collapseAllCollections = () => {
        setExpandTimestamp(Date.now());
        setExpandAll(false);
    };

    const processedCollections = () => {
        if (!pdfStore.collections || !pdfStore.collections.length) {
            return [];
        }

        return pdfStore.collections
            .filter((collection) => {
                // Filter out collections that are not from the current site
                return siteInfo?.siteId === collection.siteInfo?.siteId;
            })
            .toSorted((a, b) => {
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });
    };

    const CollapseExpandButtons = () => (
        <div class="flex shrink-0 flex-wrap gap-cvi-3">
            <Button
                label={localize('pdf-builder-collapse-all', 'Collapse all')}
                variant='ghost'
                small={true}
                onClick={collapseAllCollections}
                noBlockSpacing={true}
            />

            <Button
                label={localize('pdf-builder-expand-all', 'Expand all')}
                variant='ghost'
                small={true}
                onClick={expandAllCollections}
                noBlockSpacing={true}
            />
        </div>
    );

    return (
        <div>
            <Show
                when={!userState.user}
                fallback={
                    <div class="mb-cvi-6 flex flex-wrap items-center justify-between gap-cvi-4">
                        <Heading tag="h2" variant='heading-lg' noBlockSpacing>
                            {localize('pdf-builder-my-documents', 'My Documents')}
                        </Heading>

                        <Show when={processedCollections().length > 1}>
                            <CollapseExpandButtons />
                        </Show>
                    </div>
                }
            >
                <div class="mb-cvi-6">
                    <Heading tag="h2" variant='heading-lg'>
                        {localize('pdf-builder-collections-sign-in-cta-header', 'Sign in to save your PDF collections')}
                    </Heading>
                    <p
                        class="m-0 body-md font-cvi-regular text-cvi-fg-muted"
                        innerHTML={localize('pdf-builder-collections-description', 'Create an account to save your PDFs and access them from other devices.')}
                    />
                </div>
            </Show>

            <Show
                when={processedCollections().length}
                fallback={
                    <EmptyState>
                        <span innerHTML={localize('pdf-builder-no-compositions', 'No created PDFs yet')} />
                    </EmptyState>
                }
            >
                <ul class="m-0 flex list-none flex-col gap-cvi-5 p-0">
                    <For each={processedCollections()}>{(collection) => (
                        <li>
                            <CollectionSingle
                                collection={collection as Collection}
                                deleteCollection={props.deleteCollection}
                                editCollection={() => props.editCollectionAsArrangement(collection)}
                                reset={props.reset}
                                isExpanded={expandAll()}
                                expandTimestamp={expandTimestamp()}
                            />
                        </li>
                    )}</For>
                </ul>
            </Show>
        </div>
    );
};
